import { Dayjs } from "dayjs";
import { ETaxDocument } from "../../../types/e-tax.type";
import {
  EtaxAutoSendAlerts,
  EtaxAutoSendRoundStatus,
  EtaxAutoSendTrigger,
} from "../../../types/etax-auto-send.type";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import { formatDateParam } from "../dateRangeParams";

type StatusMeta = { label: string; color: string };

// ---- Round status / trigger ----

export const ROUND_STATUS_META: Record<EtaxAutoSendRoundStatus, StatusMeta> = {
  RUNNING: { label: "กำลังส่ง", color: "#5C69E8" },
  POSTPONED: { label: "เลื่อน", color: "#FDA42C" },
  COMPLETED: { label: "สำเร็จ", color: "#45BE77" },
  FAILED: { label: "ล้มเหลว", color: "#FB3B3B" },
};

export const getRoundStatusMeta = (
  status: EtaxAutoSendRoundStatus | null | undefined,
): StatusMeta =>
  (status && ROUND_STATUS_META[status]) || { label: "-", color: "#6a6a6a" };

export const ROUND_TRIGGER_LABEL: Record<EtaxAutoSendTrigger, string> = {
  SCHEDULER: "ตามรอบ",
  MANUAL: "สั่งเอง",
  RETRY: "ลองส่งใหม่",
};

// ---- Settings ----

/** ISO weekdays, 1 = Monday … 7 = Sunday — the numbering the API stores. */
export const WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "จันทร์" },
  { value: 2, label: "อังคาร" },
  { value: 3, label: "พุธ" },
  { value: 4, label: "พฤหัสบดี" },
  { value: 5, label: "ศุกร์" },
  { value: 6, label: "เสาร์" },
  { value: 7, label: "อาทิตย์" },
];

/** The round starts on the hour, so the only choices are 00:00 … 23:00. */
export const HOUR_OPTIONS: string[] = Array.from(
  { length: 24 },
  (_, hour) => `${String(hour).padStart(2, "0")}:00`,
);

/**
 * The hour option matching a stored start time. The API speaks `HH:mm`, but a `time` column
 * can come back as `HH:mm:ss`, and anything unparseable falls back to the default 03:00.
 */
export const toHourOption = (startTime: string | null | undefined): string => {
  const match = /^(\d{1,2}):/.exec(startTime ?? "");
  const hour = match ? Number(match[1]) : NaN;
  return hour >= 0 && hour <= 23 ? HOUR_OPTIONS[hour] : "03:00";
};

// ---- Alerts strip ----

export type AlertKey = keyof Pick<
  EtaxAutoSendAlerts,
  | "rd_reject_unacknowledged"
  | "ktb_rejected_unacknowledged"
  | "signed_fail_unacknowledged"
  | "held_count"
  | "blocked_count"
  | "expired_count"
  | "ktb_transient_exhausted_unacknowledged"
  | "ktb_transient_retrying"
>;

/**
 * `alert` cards turn red while they count anything — each is something a person has to act
 * on. An `info` card only reports progress the system is making on its own, and a `neutral`
 * card a settled fact nothing in eTax can change any more, so neither ever does.
 */
export type AlertCardTone = "alert" | "info" | "neutral";

export interface AlertCardMeta {
  key: AlertKey;
  label: string;
  filter: string;
  tone: AlertCardTone;
  /**
   * Whether the count follows the dashboard's date range. Held and expired bills are counted
   * per period; the blocked, acknowledgement and retry queues stay company-wide — a refused
   * bill or a rejection from last month still needs fixing or acknowledging this month.
   */
  dateRanged: boolean;
  /** The card is hidden while the backend does not send its count (an older API). */
  hideWhenAbsent?: boolean;
}

/** Caption under a card whose count follows the dashboard's date range. */
export const DATE_RANGED_CAPTION = "ตามช่วงวันที่";

/** Each card and the documents filter that lists exactly the rows it counts. */
export const ALERT_CARDS: AlertCardMeta[] = [
  {
    key: "rd_reject_unacknowledged",
    label: "กรมสรรพากรปฏิเสธ (รอรับทราบ)",
    filter: "NEEDS_ACK",
    tone: "alert",
    dateRanged: false,
  },
  {
    key: "ktb_rejected_unacknowledged",
    label: "KTB ปฏิเสธ (รอรับทราบ)",
    filter: "NEEDS_ACK",
    tone: "alert",
    dateRanged: false,
  },
  {
    key: "signed_fail_unacknowledged",
    label: "ลงลายมือชื่อไม่สำเร็จ",
    filter: "NEEDS_ACK",
    tone: "alert",
    dateRanged: false,
  },
  {
    key: "ktb_transient_exhausted_unacknowledged",
    label: "ส่งไม่สำเร็จ ลองครบแล้ว",
    filter: "NEEDS_ACK",
    tone: "alert",
    dateRanged: false,
  },
  {
    key: "held_count",
    label: "ไม่ส่งอัตโนมัติ",
    filter: "HELD",
    tone: "alert",
    dateRanged: true,
  },
  {
    key: "blocked_count",
    label: "ส่งไม่ได้ (ต้องแก้)",
    filter: "BLOCKED",
    tone: "alert",
    dateRanged: false,
  },
  {
    key: "expired_count",
    label: "เลยกำหนดส่ง",
    filter: "EXPIRED",
    tone: "neutral",
    dateRanged: true,
    hideWhenAbsent: true,
  },
  {
    key: "ktb_transient_retrying",
    label: "กำลังลองส่งใหม่",
    filter: "RETRYING",
    tone: "info",
    dateRanged: false,
  },
];

/**
 * The documents list a card opens: its status filter, plus the dashboard's date range for a
 * card that counts within that range. A company-wide card opens with no range at all — the
 * details page applies no default range, so its list then holds every row the card counted.
 */
export const buildAlertCardPath = (
  card: Pick<AlertCardMeta, "filter" | "dateRanged">,
  dateFrom: Dayjs | null | undefined,
  dateTo: Dayjs | null | undefined,
): string => {
  const params = new URLSearchParams();
  if (card.dateRanged) {
    const from = formatDateParam(dateFrom);
    const to = formatDateParam(dateTo);
    if (from) params.append("date_from", from);
    if (to) params.append("date_to", to);
  }
  params.append("etax_status", card.filter);
  return `/e-tax/details?${params.toString()}`;
};

/**
 * A card's figure: "-" until the alerts load (undefined), and 0 for a count the backend does
 * not send — an older API without the transient retry must not leave its cards blank.
 */
export const getAlertCardCount = (
  alerts: EtaxAutoSendAlerts | undefined,
  key: AlertKey,
): number | undefined => (alerts ? alerts[key] ?? 0 : undefined);

/**
 * The cards to show. A card whose count an older backend does not send at all is hidden —
 * until the alerts arrive too, so it never flashes up and vanishes against such a backend.
 */
export const getVisibleAlertCards = (
  alerts: EtaxAutoSendAlerts | undefined,
): AlertCardMeta[] =>
  ALERT_CARDS.filter((card) => !card.hideWhenAbsent || alerts?.[card.key] !== undefined);

// ---- Per-document auto-send state ----

type AutoSendDocumentFields = Pick<
  ETaxDocument,
  | "etax_status"
  | "etax_uuid"
  | "is_held"
  | "next_round_at"
  | "trigger_source"
  | "triggered_by"
  | "needs_ack"
  | "document_status"
  | "error_source"
  | "transient_retry_count"
  | "auto_retry_pending"
  | "is_expired"
> &
  // Optional so a caller holding only the auto-send fields still type-checks; absent = not paper.
  Partial<Pick<ETaxDocument, "etax_enabled">>;

export const HELD_LABEL = "ไม่ส่งอัตโนมัติ";

export const PAPER_LABEL = "กระดาษ";

/**
 * Whether the bill counts as not sent. A FAILED latest item is "not sent" everywhere — the
 * backend's ค้างส่ง / expired / hold rules treat it exactly like a bill with no item — so only
 * COMPLETED and IN_PROGRESS mean the bill is out.
 */
export const isUnsentDocument = (doc: Pick<AutoSendDocumentFields, "etax_status">): boolean =>
  !doc.etax_status || doc.etax_status === "FAILED";

/**
 * A paper bill: the shop never asked for eTax and nothing was ever sent. It is neither ค้างส่ง
 * nor expired nor held — it simply does not go through eTax.
 */
export const isPaperDocument = (doc: AutoSendDocumentFields): boolean =>
  doc.etax_enabled === false && !doc.etax_uuid;

export const EXPIRED_LABEL = "เลยกำหนดส่ง";

export const EXPIRED_TOOLTIP =
  "เลยกำหนดส่ง e-Tax รายเดือนของ KTB แล้ว ส่งผ่าน e-Tax ไม่ได้ ให้บัญชีออกเอกสารแบบกระดาษแทน";

/**
 * Whether the bill's tax month is already closed at KTB. Absent on an older API = no. A FAILED
 * bill can be expired too — it never went out.
 */
export const isExpiredDocument = (doc: AutoSendDocumentFields): boolean =>
  isUnsentDocument(doc) && doc.is_expired === true;

export type AutoSendRowState = "EXPIRED" | "HELD" | "SCHEDULED" | null;

/**
 * Where an unsent bill stands with the weekly round. Only an unsent bill (no item, or a FAILED
 * one) has one: a hold or a next-round date on a sent bill says nothing about what happens
 * next. A bill past its tax month is expired before anything else — no round or hold can send
 * it now. A paper bill has none: it never goes through eTax.
 */
export const getAutoSendRowState = (doc: AutoSendDocumentFields): AutoSendRowState => {
  if (!isUnsentDocument(doc) || isPaperDocument(doc)) return null;
  if (isExpiredDocument(doc)) return "EXPIRED";
  if (doc.is_held) return "HELD";
  if (doc.next_round_at) return "SCHEDULED";
  return null;
};

export const getScheduledLabel = (nextRoundAt: string | null | undefined): string => {
  const when = formatThaiShortDateTimeBkk(nextRoundAt);
  return when ? `รอส่งตามรอบ ${when}` : "รอส่งตามรอบ";
};

/** Who sent a sent bill — the round or a named person. Null for a bill never sent. */
export const getSentByLabel = (doc: AutoSendDocumentFields): string | null => {
  if (!doc.etax_status || !doc.trigger_source) return null;
  if (doc.trigger_source === "AUTO") return "ส่งอัตโนมัติ";
  return doc.triggered_by ? `ส่งโดย ${doc.triggered_by}` : "ส่งเอง";
};

/** The hold action is offered on a bill that is not sent (no item, or a FAILED one), and never
 * on a paper bill, which no round would pick up anyway. */
export const canHoldDocument = (doc: AutoSendDocumentFields): boolean =>
  isUnsentDocument(doc) && !isPaperDocument(doc);

/** Acknowledging needs the item it is about; `etax_uuid` is the row's latest item. */
export const canAcknowledgeDocument = (doc: AutoSendDocumentFields): boolean =>
  Boolean(doc.needs_ack && doc.etax_uuid);

/** Why a row is waiting for acknowledgement, in the same words as the alert cards. */
export const getNeedsAckReason = (doc: AutoSendDocumentFields): string => {
  if (doc.document_status === "RD_REJECT") return "กรมสรรพากรปฏิเสธ";
  if (doc.document_status === "SIGNED_FAIL") return "ลงลายมือชื่อไม่สำเร็จ";
  if (doc.error_source === "KTB_SUBMIT") return "KTB ปฏิเสธ";
  if (doc.error_source === "KTB_TRANSIENT") return "ส่งไม่สำเร็จ ลองครบแล้ว";
  return "ส่งไม่สำเร็จ";
};

// ---- Hourly transient retry ----

/** Whether the hourly sweep will re-send this bill. Absent on an older API = no. */
export const isAutoRetryPending = (doc: AutoSendDocumentFields): boolean =>
  doc.auto_retry_pending === true;

/**
 * The chip on a bill waiting for the hourly re-send: how many automatic re-sends it has used
 * out of the cap. Without a known cap it shows the count alone rather than a made-up limit.
 */
export const getAutoRetryLabel = (
  retryCount: number | null | undefined,
  retryMax: number | null | undefined,
): string => {
  const count = retryCount ?? 0;
  return retryMax === null || retryMax === undefined
    ? `รอส่งใหม่อัตโนมัติ (${count})`
    : `รอส่งใหม่อัตโนมัติ (${count}/${retryMax})`;
};
